import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';
import './ExamSchedule.css';

function ExamSchedule() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [academicYear, setAcademicYear] = useState({
    start_date: '',
    end_date: '',
    year_name: '',
    holidays: []
  });
  const [exams, setExams] = useState([]);
  const [examsByBranch, setExamsByBranch] = useState({}); // {branchId: [exams]}
  const [branches, setBranches] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedExamType, setSelectedExamType] = useState('');
  const [editingExam, setEditingExam] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form states for different exam types
  const [monthlyTestForm, setMonthlyTestForm] = useState({
    title: '',
    date: '',
    description: '',
    subjects: [],
    classId: '',
    sectionName: ''
  });
  
  const [examForm, setExamForm] = useState({
    title: '',
    start_date: '',
    end_date: '',
    description: '',
    subjects: [],
    classId: '',
    sectionName: ''
  });
  
  const [holidayForm, setHolidayForm] = useState({
    title: '',
    start_date: '',
    end_date: '',
    description: '',
    type: 'national',
    classId: '',
    sectionName: ''
  });
  
  const [otherTestForm, setOtherTestForm] = useState({
    title: '',
    date: '',
    description: '',
    test_type: '',
    subjects: [],
    classId: '',
    sectionName: ''
  });

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchBranches();
      fetchAcademicYear();
      fetchExams();
      fetchClasses();
    }
  }, [schoolData?.id, selectedBranchId]);

  // Refetch sections and subjects when classes are loaded and a class is already selected
  useEffect(() => {
    if (classes.length > 0) {
      // Check which form has a class selected and refetch sections and subjects
      if (monthlyTestForm.classId) {
        fetchSections(monthlyTestForm.classId);
        fetchSubjectsForClass(monthlyTestForm.classId);
      } else if (examForm.classId) {
        fetchSections(examForm.classId);
        fetchSubjectsForClass(examForm.classId);
      } else if (otherTestForm.classId) {
        fetchSections(otherTestForm.classId);
        fetchSubjectsForClass(otherTestForm.classId);
      } else if (holidayForm.classId) {
        fetchSections(holidayForm.classId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes.length]); // Only trigger when classes array length changes

  // Fetch branches
  const fetchBranches = async () => {
    if (!schoolData?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('branches')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('branch_name');
      
      if (error) {
        console.error('Error fetching branches:', error);
        return;
      }
      
      setBranches(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchAcademicYear = async () => {
    try {
      let query = supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        // When "All Branches" is selected, we don't filter by branch_id
        // Just get the first active academic year (or handle multiple branches as needed)
        query = query.order('branch_id', { ascending: true }).limit(1);
      }

      const { data, error } = await query.single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching academic year:', error);
        return;
      }

      if (data) {
        setAcademicYear({
          start_date: data.start_date || '',
          end_date: data.end_date || '',
          year_name: data.year_name || '',
          holidays: data.holidays || []
        });
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchExams = async () => {
    try {
      let query = supabase
        .from('exam_schedule')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        // When a specific branch is selected, show only that branch's exams
        query = query.eq('branch_id', selectedBranchId);
        
        const { data, error } = await query.order('start_date', { ascending: true });

        if (error) {
          console.error('Error fetching exams:', error);
          return;
        }

        setExams(data || []);
        setExamsByBranch({}); // Clear branch-wise exams
      } else {
        // When "All Branches" is selected, fetch all exams and group by branch
        const { data, error } = await query.order('branch_id', { ascending: true }).order('start_date', { ascending: true });

        if (error) {
          console.error('Error fetching exams:', error);
          return;
        }

        // Group exams by branch_id
        const grouped = {};
        (data || []).forEach(exam => {
          const branchId = exam.branch_id || 'no-branch';
          if (!grouped[branchId]) {
            grouped[branchId] = [];
          }
          grouped[branchId].push(exam);
        });

        setExamsByBranch(grouped);
        setExams([]); // Clear single branch exams
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query.order('class_name');

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchSections = async (classId) => {
    if (!classId) {
      setSections([]);
      return;
    }

    // Wait for classes to load if they haven't yet
    if (classes.length === 0) {
      console.log('Classes not loaded yet, retrying in 200ms...');
      setTimeout(() => fetchSections(classId), 200);
      return;
    }

    try {
      // Convert classId to number for comparison (select returns string)
      const classIdNum = typeof classId === 'string' ? parseInt(classId, 10) : Number(classId);
      
      // Find the class - sections are stored as an array in the classes table
      const selectedClass = classes.find(c => {
        const cId = Number(c.id);
        return cId === classIdNum || c.id === classId || String(c.id) === String(classId);
      });
      
      if (!selectedClass) {
        console.warn('Class not found for classId:', classId, 'Available classes:', classes.map(c => ({ id: c.id, name: c.class_name })));
        setSections([]);
        return;
      }

      console.log('Found class:', selectedClass.class_name, 'Sections data:', selectedClass.sections);

      // Sections are stored as an array in the classes table (same as ClassSchedule)
      const sectionsArray = selectedClass.sections || [];
      
      if (!Array.isArray(sectionsArray)) {
        console.warn('Sections is not an array for class:', selectedClass.class_name, 'Type:', typeof sectionsArray);
        setSections([]);
        return;
      }

      // Filter out empty sections and create section objects (exactly like ClassSchedule)
      const sectionObjects = sectionsArray
        .filter(section => section && (typeof section === 'string' ? section.trim() !== '' : true))
          .map((section, index) => ({
            id: index + 1,
          section_name: typeof section === 'string' ? section.trim() : String(section),
          class_id: selectedClass.id
          }));

      console.log('Setting sections:', sectionObjects);
        setSections(sectionObjects);
    } catch (err) {
      console.error('Error fetching sections:', err);
        setSections([]);
      }
  };

  const fetchSubjectsForClass = (classId) => {
    if (!classId) {
      setAvailableSubjects([]);
      return;
    }

    // Wait for classes to load if they haven't yet
    if (classes.length === 0) {
      setTimeout(() => fetchSubjectsForClass(classId), 200);
      return;
    }

    try {
      // Convert classId to number for comparison (select returns string)
      const classIdNum = typeof classId === 'string' ? parseInt(classId, 10) : Number(classId);
      
      // Find the class - subjects are stored as an array in the classes table
      const selectedClass = classes.find(c => {
        const cId = Number(c.id);
        return cId === classIdNum || c.id === classId || String(c.id) === String(classId);
      });
      
      if (!selectedClass) {
        console.warn('Class not found for fetching subjects, classId:', classId);
        setAvailableSubjects([]);
        return;
      }

      // Subjects are stored as an array in the classes table
      const subjectsArray = selectedClass.subjects || [];
      
      if (!Array.isArray(subjectsArray)) {
        console.warn('Subjects is not an array for class:', selectedClass.class_name, 'Type:', typeof subjectsArray);
        setAvailableSubjects([]);
        return;
      }

      // Filter out empty subjects and create subject objects
      const classSubjects = subjectsArray
        .filter(subject => subject && (typeof subject === 'string' ? subject.trim() !== '' : true))
        .map((subject, index) => ({
          id: index + 1,
          name: typeof subject === 'string' ? subject.trim() : String(subject)
        }));

      console.log('Setting available subjects for class:', selectedClass.class_name, 'Subjects:', classSubjects);
      setAvailableSubjects(classSubjects);
    } catch (err) {
      console.error('Error fetching subjects:', err);
      setAvailableSubjects([]);
    }
  };

  const handleAcademicYearSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Saving academic year with data:', {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        start_date: academicYear.start_date,
        end_date: academicYear.end_date,
        year_name: academicYear.year_name,
        holidays: academicYear.holidays,
        is_active: true
      });

      // First, try to find existing academic year for this school and branch
      let existingQuery = supabase
        .from('academic_years')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        existingQuery = existingQuery.eq('branch_id', selectedBranchId);
      } else {
        existingQuery = existingQuery.is('branch_id', null);
      }

      const { data: existingData } = await existingQuery.single();

      const academicYearData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        start_date: academicYear.start_date,
        end_date: academicYear.end_date,
        year_name: academicYear.year_name,
        holidays: academicYear.holidays,
        is_active: true,
        updated_at: new Date().toISOString()
      };

      let error;
      if (existingData) {
        // Update existing academic year
        const { error: updateError } = await supabase
          .from('academic_years')
          .update(academicYearData)
          .eq('id', existingData.id);
        error = updateError;
      } else {
        // Create new academic year
        const { error: insertError } = await supabase
          .from('academic_years')
          .insert(academicYearData);
        error = insertError;
      }

      if (error) {
        console.error('Error saving academic year:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        alert(`Failed to save academic year. Error: ${error.message || 'Please try again.'}`);
        return;
      }

      setSuccessMessage('Academic year saved successfully!');
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save academic year. Please try again.');
    }
  };

  const handleAddHoliday = () => {
    const newHoliday = {
      title: '',
      start_date: '',
      end_date: '',
      description: '',
      type: 'national'
    };
    
    setHolidayForm(newHoliday);
    setSelectedExamType('holiday');
    setShowAddForm(true);
  };

  const handleHolidaySubmit = async (e) => {
    e.preventDefault();
    try {
      const examData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        title: holidayForm.title,
        type: 'holiday',
        start_date: holidayForm.start_date,
        end_date: holidayForm.end_date,
        description: holidayForm.description,
        exam_type: holidayForm.type,
        class_id: holidayForm.classId,
        section_name: holidayForm.sectionName || null,
        is_active: true
      };

      let error;
      if (editingExam) {
        // Update existing holiday
        const { error: updateError } = await supabase
          .from('exam_schedule')
          .update(examData)
          .eq('id', editingExam.id);
        error = updateError;
      } else {
        // Create new holiday
        const { error: insertError } = await supabase
          .from('exam_schedule')
          .insert(examData);
        error = insertError;
      }

      if (error) {
        console.error('Error creating holiday:', error);
        alert('Failed to create holiday. Please try again.');
        return;
      }

      // Also add to academic year holidays (only for new holidays)
      if (!editingExam) {
        const updatedHolidays = [...academicYear.holidays, {
          title: holidayForm.title,
          start_date: holidayForm.start_date,
          end_date: holidayForm.end_date,
          description: holidayForm.description,
          type: holidayForm.type
        }];
        
        setAcademicYear({...academicYear, holidays: updatedHolidays});
        
        // Update academic year in database
        let updateQuery = supabase
          .from('academic_years')
          .update({ holidays: updatedHolidays })
          .eq('school_id', schoolData.id)
          .eq('is_active', true);

        if (selectedBranchId) {
          updateQuery = updateQuery.eq('branch_id', selectedBranchId);
        } else {
          updateQuery = updateQuery.is('branch_id', null);
        }

        await updateQuery;
      }

      resetForms();
      fetchExams();
      setSuccessMessage(editingExam ? 'Holiday updated successfully!' : 'Holiday added successfully!');
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save holiday. Please try again.');
    }
  };

  const handleMonthlyTestSubmit = async (e) => {
    e.preventDefault();
    try {
      const examData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        title: monthlyTestForm.title,
        type: 'monthly_test',
        start_date: monthlyTestForm.date,
        end_date: monthlyTestForm.date,
        description: monthlyTestForm.description,
        subjects: monthlyTestForm.subjects,
        class_id: monthlyTestForm.classId,
        section_name: monthlyTestForm.sectionName || null,
        is_active: true
      };

      let error;
      if (editingExam) {
        // Update existing exam
        const { error: updateError } = await supabase
          .from('exam_schedule')
          .update(examData)
          .eq('id', editingExam.id);
        error = updateError;
      } else {
        // Create new exam
        const { error: insertError } = await supabase
          .from('exam_schedule')
          .insert(examData);
        error = insertError;
      }

      if (error) {
        console.error('Error creating monthly test:', error);
        alert('Failed to create monthly test. Please try again.');
        return;
      }

      resetForms();
      fetchExams();
      setSuccessMessage(editingExam ? 'Monthly test updated successfully!' : 'Monthly test added successfully!');
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save monthly test. Please try again.');
    }
  };

  const handleExamSubmit = async (e) => {
    e.preventDefault();
    try {
      const examData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        title: examForm.title,
        type: 'exam',
        start_date: examForm.start_date,
        end_date: examForm.end_date,
        description: examForm.description,
        subjects: examForm.subjects,
        class_id: examForm.classId,
        section_name: examForm.sectionName || null,
        is_active: true
      };

      let error;
      if (editingExam) {
        // Update existing exam
        const { error: updateError } = await supabase
          .from('exam_schedule')
          .update(examData)
          .eq('id', editingExam.id);
        error = updateError;
      } else {
        // Create new exam
        const { error: insertError } = await supabase
          .from('exam_schedule')
          .insert(examData);
        error = insertError;
      }

      if (error) {
        console.error('Error creating exam:', error);
        alert('Failed to create exam. Please try again.');
        return;
      }

      resetForms();
      fetchExams();
      setSuccessMessage(editingExam ? 'Exam updated successfully!' : 'Exam added successfully!');
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save exam. Please try again.');
    }
  };

  const handleOtherTestSubmit = async (e) => {
    e.preventDefault();
    try {
      const examData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId || null,
        title: otherTestForm.title,
        type: 'other_test',
        start_date: otherTestForm.date,
        end_date: otherTestForm.date,
        description: otherTestForm.description,
        exam_type: otherTestForm.test_type,
        subjects: otherTestForm.subjects,
        class_id: otherTestForm.classId,
        section_name: otherTestForm.sectionName || null,
        is_active: true
      };

      let error;
      if (editingExam) {
        // Update existing exam
        const { error: updateError } = await supabase
          .from('exam_schedule')
          .update(examData)
          .eq('id', editingExam.id);
        error = updateError;
      } else {
        // Create new exam
        const { error: insertError } = await supabase
          .from('exam_schedule')
          .insert(examData);
        error = insertError;
      }

      if (error) {
        console.error('Error creating other test:', error);
        alert('Failed to create other test. Please try again.');
        return;
      }

      resetForms();
      fetchExams();
      setSuccessMessage(editingExam ? 'Test updated successfully!' : 'Test added successfully!');
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save test. Please try again.');
    }
  };

  const resetForms = () => {
    setShowAddForm(false);
    setSelectedExamType('');
    setEditingExam(null);
    setMonthlyTestForm({ title: '', date: '', description: '', subjects: [], classId: '', sectionName: '' });
    setExamForm({ title: '', start_date: '', end_date: '', description: '', subjects: [], classId: '', sectionName: '' });
    setHolidayForm({ title: '', start_date: '', end_date: '', description: '', type: 'national', classId: '', sectionName: '' });
    setOtherTestForm({ title: '', date: '', description: '', test_type: '', subjects: [], classId: '', sectionName: '' });
    setSections([]);
    setAvailableSubjects([]);
  };

  const handleDeleteExam = (examId) => {
    setExamToDelete(examId);
    setShowDeleteConfirmPopup(true);
  };

  const confirmDeleteExam = async () => {
    if (!examToDelete) return;

    try {
      let deleteQuery = supabase
        .from('exam_schedule')
        .update({ is_active: false })
        .eq('id', examToDelete);

      if (selectedBranchId) {
        deleteQuery = deleteQuery.eq('branch_id', selectedBranchId);
      } else {
        deleteQuery = deleteQuery.is('branch_id', null);
      }

      const { error } = await deleteQuery;

      if (error) {
        console.error('Error deleting exam:', error);
        setShowDeleteConfirmPopup(false);
        setExamToDelete(null);
        alert('Failed to delete exam. Please try again.');
        return;
      }

      fetchExams();
      setShowDeleteConfirmPopup(false);
      setExamToDelete(null);
      setShowDeleteSuccessPopup(true);
      
      // Auto-hide success popup after 3 seconds
      setTimeout(() => {
        setShowDeleteSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error:', err);
      setShowDeleteConfirmPopup(false);
      setExamToDelete(null);
      alert('Failed to delete exam. Please try again.');
    }
  };

  const cancelDeleteExam = () => {
    setShowDeleteConfirmPopup(false);
    setExamToDelete(null);
  };

  const handleEditExam = (exam) => {
    setEditingExam(exam);
    setShowAddForm(true);
    
    // Fetch sections and subjects for the exam's class if it has one
    if (exam.class_id) {
      fetchSections(exam.class_id);
      fetchSubjectsForClass(exam.class_id);
    }
    
    // Set the appropriate exam type and populate the form
    switch (exam.type) {
      case 'monthly_test':
        setSelectedExamType('monthly');
        setMonthlyTestForm({
          title: exam.title,
          date: exam.start_date,
          description: exam.description || '',
          subjects: exam.subjects || [],
          classId: exam.class_id || '',
          sectionName: exam.section_name || ''
        });
        break;
      case 'exam':
        setSelectedExamType('exam');
        setExamForm({
          title: exam.title,
          start_date: exam.start_date,
          end_date: exam.end_date,
          description: exam.description || '',
          subjects: exam.subjects || [],
          classId: exam.class_id || '',
          sectionName: exam.section_name || ''
        });
        break;
      case 'holiday':
        setSelectedExamType('holiday');
        setHolidayForm({
          title: exam.title,
          start_date: exam.start_date,
          end_date: exam.end_date,
          description: exam.description || '',
          type: exam.exam_type || 'national',
          classId: exam.class_id || '',
          sectionName: exam.section_name || ''
        });
        break;
      case 'other_test':
        setSelectedExamType('other');
        setOtherTestForm({
          title: exam.title,
          date: exam.start_date,
          description: exam.description || '',
          test_type: exam.exam_type || '',
          subjects: exam.subjects || [],
          classId: exam.class_id || '',
          sectionName: exam.section_name || ''
        });
        break;
      default:
        console.error('Unknown exam type:', exam.type);
    }
  };

  const addSubject = (formType) => {
    const newSubject = { name: '', date: '', time: '' };
    
    switch (formType) {
      case 'monthly':
        setMonthlyTestForm({
          ...monthlyTestForm,
          subjects: [...monthlyTestForm.subjects, newSubject]
        });
        break;
      case 'exam':
        setExamForm({
          ...examForm,
          subjects: [...examForm.subjects, newSubject]
        });
        break;
      case 'other':
        setOtherTestForm({
          ...otherTestForm,
          subjects: [...otherTestForm.subjects, newSubject]
        });
        break;
    }
  };

  const removeSubject = (formType, index) => {
    switch (formType) {
      case 'monthly':
        setMonthlyTestForm({
          ...monthlyTestForm,
          subjects: monthlyTestForm.subjects.filter((_, i) => i !== index)
        });
        break;
      case 'exam':
        setExamForm({
          ...examForm,
          subjects: examForm.subjects.filter((_, i) => i !== index)
        });
        break;
      case 'other':
        setOtherTestForm({
          ...otherTestForm,
          subjects: otherTestForm.subjects.filter((_, i) => i !== index)
        });
        break;
    }
  };

  const updateSubject = (formType, index, field, value) => {
    switch (formType) {
      case 'monthly':
        const updatedMonthlySubjects = [...monthlyTestForm.subjects];
        updatedMonthlySubjects[index][field] = value;
        setMonthlyTestForm({
          ...monthlyTestForm,
          subjects: updatedMonthlySubjects
        });
        break;
      case 'exam':
        const updatedExamSubjects = [...examForm.subjects];
        updatedExamSubjects[index][field] = value;
        setExamForm({
          ...examForm,
          subjects: updatedExamSubjects
        });
        break;
      case 'other':
        const updatedOtherSubjects = [...otherTestForm.subjects];
        updatedOtherSubjects[index][field] = value;
        setOtherTestForm({
          ...otherTestForm,
          subjects: updatedOtherSubjects
        });
        break;
    }
  };

  const getExamTypeIcon = (type) => {
    switch (type) {
      case 'monthly_test': return '📝';
      case 'exam': return '📋';
      case 'holiday': return '🎉';
      case 'other_test': return '📊';
      default: return '📅';
    }
  };

  const getExamTypeLabel = (type) => {
    switch (type) {
      case 'monthly_test': return 'Monthly Test';
      case 'exam': return 'Exam';
      case 'holiday': return 'Holiday';
      case 'other_test': return 'Other Test';
      default: return 'Other';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="exam-schedule-container">
          {/* Page Header */}
        <div className="exam-schedule-header">
          <div className="exam-schedule-header-left">
            <svg className="exam-schedule-header-icon" width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h1 className="exam-schedule-title">Exam Schedule</h1>
          </div>
          <p className="exam-schedule-description">Manage academic year and schedule exams, tests, and holidays</p>
        </div>
        <div className="dashboard-branch-row exam-schedule-branch-row">
          <BranchSelector
            schoolId={schoolData?.id}
            selectedBranchId={selectedBranchId}
            onBranchChange={handleBranchChange}
          />
          <div className="branch-context">
            {selectedBranchId ? 'Showing branch-specific schedule & exams' : 'Showing school-wide schedule & exams'}
          </div>
        </div>
          {/* Academic Year Section */}
          <div className="academic-year-section">
            <div className="academic-year-card">
              <div className="academic-year-header">
                <svg className="academic-year-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 6.25278V19.2528M12 6.25278C10.8321 5.47686 9.24649 5 7.5 5C5.75351 5 4.16789 5.47686 3 6.25278V19.2528C4.16789 18.4769 5.75351 18 7.5 18C9.24649 18 10.8321 18.4769 12 19.2528M12 6.25278C13.1679 5.47686 14.7535 5 16.5 5C18.2465 5 19.8321 5.47686 21 6.25278V19.2528C19.8321 18.4769 18.2465 18 16.5 18C14.7535 18 13.1679 18.4769 12 19.2528" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h2 className="academic-year-title">Academic Year Setup</h2>
              </div>
              <form onSubmit={handleAcademicYearSubmit} className="academic-year-form">
                  <div className="form-group">
                  <label htmlFor="year-name">
                    Academic Year Name <span className="required-asterisk">*</span>
                  </label>
                    <input
                      type="text"
                      id="year-name"
                      value={academicYear.year_name}
                      onChange={(e) => setAcademicYear({...academicYear, year_name: e.target.value})}
                      placeholder="e.g., 2024-2025"
                      className="form-input"
                      required
                    />
                </div>

                <div className="date-inputs-grid">
                  <div className="form-group">
                    <label htmlFor="start-date">
                      Start Date <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="date"
                      id="start-date"
                      value={academicYear.start_date}
                      onChange={(e) => setAcademicYear({...academicYear, start_date: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="end-date">
                      End Date <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="date"
                      id="end-date"
                      value={academicYear.end_date}
                      onChange={(e) => setAcademicYear({...academicYear, end_date: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <button type="submit" className="save-academic-year-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 21H5C4.44772 21 4 20.5523 4 20V4C4 3.44772 4.44772 3 5 3H16L20 7V20C20 20.5523 19.5523 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Save Academic Year
                </button>
              </form>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="quick-actions-section">
            <div className="quick-actions-header">
              <svg className="quick-actions-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h2 className="quick-actions-title">Quick Actions</h2>
            </div>
              <div className="quick-actions-grid">
                <button
                  onClick={() => {
                    setSelectedExamType('monthly_test');
                    setShowAddForm(true);
                  }}
                className="quick-action-btn"
                >
                <span className="quick-action-icon">📝</span>
                <span className="quick-action-label">Add Monthly Test</span>
                </button>
                
                <button
                  onClick={() => {
                    setSelectedExamType('exam');
                    setShowAddForm(true);
                  }}
                className="quick-action-btn"
                >
                <span className="quick-action-icon">📋</span>
                <span className="quick-action-label">Add Exam</span>
                </button>
                
                <button
                  onClick={handleAddHoliday}
                className="quick-action-btn"
                >
                <span className="quick-action-icon">🎉</span>
                <span className="quick-action-label">Add Holiday</span>
                </button>
                
                <button
                  onClick={() => {
                    setSelectedExamType('other_test');
                    setShowAddForm(true);
                  }}
                className="quick-action-btn"
                >
                <span className="quick-action-icon">📊</span>
                <span className="quick-action-label">Add Other Test</span>
                </button>
            </div>
          </div>

          {/* Add Forms Modal */}
          {showAddForm && (
            <div className="exam-modal-overlay" onClick={resetForms}>
              <div className="exam-modal" onClick={(e) => e.stopPropagation()}>
                <div className="exam-modal-header">
                  <div className="exam-modal-header-left">
                    {selectedExamType === 'monthly_test' && (
                      <>
                        <svg className="exam-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <h2 className="exam-modal-title">Add Monthly Test</h2>
                      </>
                    )}
                    {selectedExamType === 'exam' && (
                      <>
                        <svg className="exam-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <h2 className="exam-modal-title">Add Exam</h2>
                      </>
                    )}
                    {selectedExamType === 'holiday' && (
                      <>
                        <svg className="exam-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 6L9 17L4 12" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <h2 className="exam-modal-title">Add Holiday</h2>
                      </>
                    )}
                    {selectedExamType === 'other_test' && (
                      <>
                        <svg className="exam-modal-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="#FF8B3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <h2 className="exam-modal-title">Add Other Test</h2>
                      </>
                    )}
                  </div>
                  <button onClick={resetForms} className="exam-modal-close-btn">×</button>
                </div>

                {/* Monthly Test Form */}
                {selectedExamType === 'monthly_test' && (
                  <form onSubmit={handleMonthlyTestSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="monthly-title">Test Title *</label>
                        <input
                          type="text"
                          id="monthly-title"
                          value={monthlyTestForm.title}
                          onChange={(e) => setMonthlyTestForm({...monthlyTestForm, title: e.target.value})}
                          placeholder="e.g., Monthly Test - January"
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="monthly-date">Date *</label>
                        <input
                          type="date"
                          id="monthly-date"
                          value={monthlyTestForm.date}
                          onChange={(e) => setMonthlyTestForm({...monthlyTestForm, date: e.target.value})}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="monthly-description">Description</label>
                      <textarea
                        id="monthly-description"
                        value={monthlyTestForm.description}
                        onChange={(e) => setMonthlyTestForm({...monthlyTestForm, description: e.target.value})}
                        placeholder="Additional details about the test..."
                        className="form-textarea"
                        rows="3"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="monthly-class">Class *</label>
                        <select
                          id="monthly-class"
                          value={monthlyTestForm.classId}
                          onChange={(e) => {
                            setMonthlyTestForm({...monthlyTestForm, classId: e.target.value, sectionName: ''});
                            fetchSections(e.target.value);
                            fetchSubjectsForClass(e.target.value);
                          }}
                          className="form-input"
                          required
                        >
                          <option value="">Select Class</option>
                          {classes.map((cls) => (
                            <option key={cls.id} value={cls.id}>
                              {cls.class_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="monthly-section">Section (Optional)</label>
                        <select
                          id="monthly-section"
                          value={monthlyTestForm.sectionName}
                          onChange={(e) => setMonthlyTestForm({...monthlyTestForm, sectionName: e.target.value})}
                          className="form-input"
                          disabled={!monthlyTestForm.classId}
                        >
                          <option value="">{sections.length === 0 && monthlyTestForm.classId ? 'No sections available' : 'All Sections'}</option>
                          {sections.length > 0 && sections.map((section) => (
                            <option key={section.id || section.section_name} value={section.section_name}>
                              {section.section_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="subjects-section">
                      <div className="subjects-header">
                        <h4 className="subjects-section-title">Subjects</h4>
                        <button
                          type="button"
                          onClick={() => addSubject('monthly')}
                          className="add-subject-btn"
                        >
                          + Add Subject
                        </button>
                      </div>

                      {monthlyTestForm.subjects.length === 0 ? (
                        <div className="no-subjects">
                          <p>No subjects added yet. Click "Add Subject" to start.</p>
                        </div>
                      ) : (
                        <div className="subjects-list">
                          {monthlyTestForm.subjects.map((subject, index) => (
                            <div key={index} className="subject-entry">
                              <div className="subject-header">
                                <span className="subject-number">Subject {index + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => removeSubject('monthly', index)}
                                  className="remove-subject-btn"
                                >
                                  🗑️
                                </button>
                              </div>
                              
                              <div className="subject-fields">
                                <div className="form-group">
                                  <label>Subject Name *</label>
                                  <select
                                    value={subject.name}
                                    onChange={(e) => updateSubject('monthly', index, 'name', e.target.value)}
                                    className="form-input"
                                    required
                                    disabled={!monthlyTestForm.classId || availableSubjects.length === 0}
                                  >
                                    <option value="">
                                      {!monthlyTestForm.classId 
                                        ? 'Select Class First' 
                                        : availableSubjects.length === 0 
                                          ? 'No subjects available' 
                                          : 'Select Subject'}
                                    </option>
                                    {availableSubjects.length > 0 && availableSubjects.map((subj) => (
                                      <option key={subj.id} value={subj.name}>
                                        {subj.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                
                                <div className="form-group">
                                  <label>Date *</label>
                                  <input
                                    type="date"
                                    value={subject.date}
                                    onChange={(e) => updateSubject('monthly', index, 'date', e.target.value)}
                                    className="form-input"
                                    required
                                  />
                                </div>
                                
                                <div className="form-group">
                                  <label>Time *</label>
                                  <input
                                    type="time"
                                    value={subject.time}
                                    onChange={(e) => updateSubject('monthly', index, 'time', e.target.value)}
                                    className="form-input"
                                    required
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="exam-modal-actions">
                      <button type="submit" className="exam-modal-submit-btn">
                        Add Monthly Test
                      </button>
                      <button type="button" onClick={resetForms} className="exam-modal-cancel-btn">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Exam Form */}
                {selectedExamType === 'exam' && (
                  <form onSubmit={handleExamSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="exam-title">Exam Title *</label>
                        <input
                          type="text"
                          id="exam-title"
                          value={examForm.title}
                          onChange={(e) => setExamForm({...examForm, title: e.target.value})}
                          placeholder="e.g., Mid-term Exam"
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="exam-start-date">Start Date *</label>
                        <input
                          type="date"
                          id="exam-start-date"
                          value={examForm.start_date}
                          onChange={(e) => setExamForm({...examForm, start_date: e.target.value})}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="exam-end-date">End Date *</label>
                        <input
                          type="date"
                          id="exam-end-date"
                          value={examForm.end_date}
                          onChange={(e) => setExamForm({...examForm, end_date: e.target.value})}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="exam-description">Description</label>
                      <textarea
                        id="exam-description"
                        value={examForm.description}
                        onChange={(e) => setExamForm({...examForm, description: e.target.value})}
                        placeholder="Additional details about the exam..."
                        className="form-textarea"
                        rows="3"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="exam-class">Class *</label>
                        <select
                          id="exam-class"
                          value={examForm.classId}
                          onChange={(e) => {
                            setExamForm({...examForm, classId: e.target.value, sectionName: ''});
                            fetchSections(e.target.value);
                            fetchSubjectsForClass(e.target.value);
                          }}
                          className="form-input"
                          required
                        >
                          <option value="">Select Class</option>
                          {classes.map((cls) => (
                            <option key={cls.id} value={cls.id}>
                              {cls.class_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="exam-section">Section (Optional)</label>
                        <select
                          id="exam-section"
                          value={examForm.sectionName}
                          onChange={(e) => setExamForm({...examForm, sectionName: e.target.value})}
                          className="form-input"
                          disabled={!examForm.classId}
                        >
                          <option value="">{sections.length === 0 && examForm.classId ? 'No sections available' : 'All Sections'}</option>
                          {sections.length > 0 && sections.map((section) => (
                            <option key={section.id || section.section_name} value={section.section_name}>
                              {section.section_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="subjects-section">
                      <div className="subjects-header">
                        <h4 className="subjects-section-title">Subjects</h4>
                        <button
                          type="button"
                          onClick={() => addSubject('exam')}
                          className="add-subject-btn"
                        >
                          + Add Subject
                        </button>
                      </div>

                      {examForm.subjects.length === 0 ? (
                        <div className="no-subjects">
                          <p>No subjects added yet. Click "Add Subject" to start.</p>
                        </div>
                      ) : (
                        <div className="subjects-list">
                          {examForm.subjects.map((subject, index) => (
                            <div key={index} className="subject-entry">
                              <div className="subject-header">
                                <span className="subject-number">Subject {index + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => removeSubject('exam', index)}
                                  className="remove-subject-btn"
                                >
                                  🗑️
                                </button>
                              </div>
                              
                              <div className="subject-fields">
                                <div className="form-group">
                                  <label>Subject Name *</label>
                                  <select
                                    value={subject.name}
                                    onChange={(e) => updateSubject('exam', index, 'name', e.target.value)}
                                    className="form-input"
                                    required
                                    disabled={!examForm.classId || availableSubjects.length === 0}
                                  >
                                    <option value="">
                                      {!examForm.classId 
                                        ? 'Select Class First' 
                                        : availableSubjects.length === 0 
                                          ? 'No subjects available' 
                                          : 'Select Subject'}
                                    </option>
                                    {availableSubjects.length > 0 && availableSubjects.map((subj) => (
                                      <option key={subj.id} value={subj.name}>
                                        {subj.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                
                                <div className="form-group">
                                  <label>Date *</label>
                                  <input
                                    type="date"
                                    value={subject.date}
                                    onChange={(e) => updateSubject('exam', index, 'date', e.target.value)}
                                    className="form-input"
                                    required
                                  />
                                </div>
                                
                                <div className="form-group">
                                  <label>Time *</label>
                                  <input
                                    type="time"
                                    value={subject.time}
                                    onChange={(e) => updateSubject('exam', index, 'time', e.target.value)}
                                    className="form-input"
                                    required
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="exam-modal-actions">
                      <button type="submit" className="exam-modal-submit-btn">
                        Add Exam
                      </button>
                      <button type="button" onClick={resetForms} className="exam-modal-cancel-btn">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Holiday Form */}
                {selectedExamType === 'holiday' && (
                  <form onSubmit={handleHolidaySubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="holiday-title">Holiday Title *</label>
                        <input
                          type="text"
                          id="holiday-title"
                          value={holidayForm.title}
                          onChange={(e) => setHolidayForm({...holidayForm, title: e.target.value})}
                          placeholder="e.g., Eid-ul-Fitr"
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="holiday-type">Holiday Type *</label>
                        <select
                          id="holiday-type"
                          value={holidayForm.type}
                          onChange={(e) => setHolidayForm({...holidayForm, type: e.target.value})}
                          className="form-select"
                          required
                        >
                          <option value="national">National Holiday</option>
                          <option value="religious">Religious Holiday</option>
                          <option value="school">School Holiday</option>
                          <option value="exam">Exam Holiday</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="holiday-start-date">Start Date *</label>
                        <input
                          type="date"
                          id="holiday-start-date"
                          value={holidayForm.start_date}
                          onChange={(e) => setHolidayForm({...holidayForm, start_date: e.target.value})}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="holiday-end-date">End Date *</label>
                        <input
                          type="date"
                          id="holiday-end-date"
                          value={holidayForm.end_date}
                          onChange={(e) => setHolidayForm({...holidayForm, end_date: e.target.value})}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="holiday-description">Description</label>
                      <textarea
                        id="holiday-description"
                        value={holidayForm.description}
                        onChange={(e) => setHolidayForm({...holidayForm, description: e.target.value})}
                        placeholder="Additional details about the holiday..."
                        className="form-textarea"
                        rows="3"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="holiday-class">Class *</label>
                        <select
                          id="holiday-class"
                          value={holidayForm.classId}
                          onChange={(e) => {
                            setHolidayForm({...holidayForm, classId: e.target.value, sectionName: ''});
                            fetchSections(e.target.value);
                          }}
                          className="form-input"
                          required
                        >
                          <option value="">Select Class</option>
                          {classes.map((cls) => (
                            <option key={cls.id} value={cls.id}>
                              {cls.class_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="holiday-section">Section (Optional)</label>
                        <select
                          id="holiday-section"
                          value={holidayForm.sectionName}
                          onChange={(e) => setHolidayForm({...holidayForm, sectionName: e.target.value})}
                          className="form-input"
                          disabled={!holidayForm.classId}
                        >
                          <option value="">{sections.length === 0 && examForm.classId ? 'No sections available' : 'All Sections'}</option>
                          {sections.length > 0 && sections.map((section) => (
                            <option key={section.id || section.section_name} value={section.section_name}>
                              {section.section_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="exam-modal-actions">
                      <button type="submit" className="exam-modal-submit-btn">
                        Add Holiday
                      </button>
                      <button type="button" onClick={resetForms} className="exam-modal-cancel-btn">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Other Test Form */}
                {selectedExamType === 'other_test' && (
                  <form onSubmit={handleOtherTestSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="other-title">Test Title *</label>
                        <input
                          type="text"
                          id="other-title"
                          value={otherTestForm.title}
                          onChange={(e) => setOtherTestForm({...otherTestForm, title: e.target.value})}
                          placeholder="e.g., Quiz Competition"
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="test-type">Test Type *</label>
                        <input
                          type="text"
                          id="test-type"
                          value={otherTestForm.test_type}
                          onChange={(e) => setOtherTestForm({...otherTestForm, test_type: e.target.value})}
                          placeholder="e.g., Quiz, Competition, Assessment"
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="other-date">Date *</label>
                        <input
                          type="date"
                          id="other-date"
                          value={otherTestForm.date}
                          onChange={(e) => setOtherTestForm({...otherTestForm, date: e.target.value})}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="other-description">Description</label>
                      <textarea
                        id="other-description"
                        value={otherTestForm.description}
                        onChange={(e) => setOtherTestForm({...otherTestForm, description: e.target.value})}
                        placeholder="Additional details about the test..."
                        className="form-textarea"
                        rows="3"
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="other-class">Class *</label>
                        <select
                          id="other-class"
                          value={otherTestForm.classId}
                          onChange={(e) => {
                            setOtherTestForm({...otherTestForm, classId: e.target.value, sectionName: ''});
                            fetchSections(e.target.value);
                            fetchSubjectsForClass(e.target.value);
                          }}
                          className="form-input"
                          required
                        >
                          <option value="">Select Class</option>
                          {classes.map((cls) => (
                            <option key={cls.id} value={cls.id}>
                              {cls.class_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="other-section">Section (Optional)</label>
                        <select
                          id="other-section"
                          value={otherTestForm.sectionName}
                          onChange={(e) => setOtherTestForm({...otherTestForm, sectionName: e.target.value})}
                          className="form-input"
                          disabled={!otherTestForm.classId}
                        >
                          <option value="">{sections.length === 0 && examForm.classId ? 'No sections available' : 'All Sections'}</option>
                          {sections.length > 0 && sections.map((section) => (
                            <option key={section.id || section.section_name} value={section.section_name}>
                              {section.section_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="subjects-section">
                      <div className="subjects-header">
                        <h4 className="subjects-section-title">Subjects/Topics</h4>
                        <button
                          type="button"
                          onClick={() => addSubject('other')}
                          className="add-subject-btn"
                        >
                          + Add Subject
                        </button>
                      </div>

                      {otherTestForm.subjects.length === 0 ? (
                        <div className="no-subjects">
                          <p>No subjects added yet. Click "Add Subject" to start.</p>
                        </div>
                      ) : (
                        <div className="subjects-list">
                          {otherTestForm.subjects.map((subject, index) => (
                            <div key={index} className="subject-entry">
                              <div className="subject-header">
                                <span className="subject-number">Subject {index + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => removeSubject('other', index)}
                                  className="remove-subject-btn"
                                >
                                  🗑️
                                </button>
                              </div>
                              
                              <div className="subject-fields">
                                <div className="form-group">
                                  <label>Subject/Topic Name *</label>
                                  <select
                                    value={subject.name}
                                    onChange={(e) => updateSubject('other', index, 'name', e.target.value)}
                                    className="form-input"
                                    required
                                    disabled={!otherTestForm.classId || availableSubjects.length === 0}
                                  >
                                    <option value="">
                                      {!otherTestForm.classId 
                                        ? 'Select Class First' 
                                        : availableSubjects.length === 0 
                                          ? 'No subjects available' 
                                          : 'Select Subject'}
                                    </option>
                                    {availableSubjects.length > 0 && availableSubjects.map((subj) => (
                                      <option key={subj.id} value={subj.name}>
                                        {subj.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                
                                <div className="form-group">
                                  <label>Date *</label>
                                  <input
                                    type="date"
                                    value={subject.date}
                                    onChange={(e) => updateSubject('other', index, 'date', e.target.value)}
                                    className="form-input"
                                    required
                                  />
                                </div>
                                
                                <div className="form-group">
                                  <label>Time *</label>
                                  <input
                                    type="time"
                                    value={subject.time}
                                    onChange={(e) => updateSubject('other', index, 'time', e.target.value)}
                                    className="form-input"
                                    required
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="exam-modal-actions">
                      <button type="submit" className="exam-modal-submit-btn">
                        Add Test
                      </button>
                      <button type="button" onClick={resetForms} className="exam-modal-cancel-btn">
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}

          {/* Exams List */}
          <div className="exams-section">
            <div className="exams-stats-header">
              <h2 className="exams-stats-title">Scheduled Exams & Events</h2>
              <div className="exams-stats-badges">
                {(() => {
                  // Calculate stats from exams or examsByBranch
                  let allExams = exams;
                  if (!selectedBranchId && Object.keys(examsByBranch).length > 0) {
                    // When "All Branches" is selected, flatten examsByBranch
                    allExams = Object.values(examsByBranch).flat();
                  }
                  
                  return (
                    <>
                      <div className="exam-stat-badge">
                        <span className="exam-stat-number">{allExams.filter(e => e.type === 'monthly_test').length}</span>
                        <span className="exam-stat-label">MONTHLY TESTS</span>
                      </div>
                      <div className="exam-stat-badge">
                        <span className="exam-stat-number">{allExams.filter(e => e.type === 'exam').length}</span>
                        <span className="exam-stat-label">EXAMS</span>
                      </div>
                      <div className="exam-stat-badge">
                        <span className="exam-stat-number">{allExams.filter(e => e.type === 'holiday').length}</span>
                        <span className="exam-stat-label">HOLIDAYS</span>
                      </div>
                      <div className="exam-stat-badge">
                        <span className="exam-stat-number">{allExams.filter(e => e.type === 'other_test').length}</span>
                        <span className="exam-stat-label">OTHER TESTS</span>
                      </div>
                    </>
                  );
                })()}
                </div>
              </div>

              <div className="exams-list">
                {(() => {
                  // When "All Branches" is selected and we have branch-wise data
                  if (!selectedBranchId && Object.keys(examsByBranch).length > 0) {
                    const allBranchesData = Object.values(examsByBranch).flat();
                    if (allBranchesData.length === 0) {
                      return (
                        <div className="no-exams">
                          <div className="no-exams-icon">📅</div>
                          <h3>No Exams Scheduled</h3>
                          <p>Start by setting up your academic year and adding exams, tests, and holidays.</p>
                        </div>
                      );
                    }

                    // Render exams grouped by branch
                    return (
                      <div className="exams-by-branch-container">
                        {Object.entries(examsByBranch).map(([branchId, branchExams]) => {
                          const branch = branchId === 'no-branch' ? null : branches.find(b => b.id === parseInt(branchId));
                          const branchName = branch ? branch.branch_name : 'Unassigned';
                          
                          return (
                            <div key={branchId} className="exams-branch-group">
                              <h3 className="exams-branch-title">
                                {branchName} ({branchExams.length} {branchExams.length === 1 ? 'exam' : 'exams'})
                              </h3>
                              <div className="exams-grid">
                                {branchExams.map((exam) => (
                                  <div key={exam.id} className="exam-event-card">
                                    <div className="exam-event-header">
                                      <span className="exam-event-type-badge">
                                        {getExamTypeLabel(exam.type).toUpperCase()}
                                      </span>
                                      <div className="exam-event-actions">
                                        <button
                                          onClick={() => handleEditExam(exam)}
                                          className="exam-edit-btn"
                                          title="Edit Exam/Event"
                                        >
                                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        </button>
                                        <button
                                          onClick={() => handleDeleteExam(exam.id)}
                                          className="exam-delete-btn"
                                          title="Delete Exam/Event"
                                        >
                                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                    
                                    <h4 className="exam-event-title">{exam.title}</h4>
                                    <p className="exam-event-date-range">
                                      {exam.start_date === exam.end_date 
                                        ? formatDate(exam.start_date)
                                        : `${formatDate(exam.start_date)} - ${formatDate(exam.end_date)}`
                                      }
                                    </p>
                                    {(exam.class_id || exam.section_name) && (
                                      <p className="exam-event-assigned">
                                        <strong>Assigned to:</strong> {
                                          exam.class_id && exam.section_name 
                                            ? `${classes.find(c => c.id === exam.class_id)?.class_name || 'Unknown Class'} - Section ${exam.section_name}`
                                            : exam.class_id 
                                              ? `${classes.find(c => c.id === exam.class_id)?.class_name || 'Unknown Class'}`
                                              : `Section ${exam.section_name}`
                                        }
                                      </p>
                                    )}
                                    {exam.subjects && exam.subjects.length > 0 && (
                                      <div className="exam-event-subjects">
                                        {exam.subjects.map((subject, index) => (
                                          <div key={index} className="exam-event-subject-row">
                                            <span className="exam-event-subject-name">{subject.name}</span>
                                            <span className="exam-event-subject-datetime">
                                              {formatDate(subject.date)} • {subject.time}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }

                  // Single branch view (or no exams)
                  if (exams.length === 0) {
                    return (
                      <div className="no-exams">
                        <div className="no-exams-icon">📅</div>
                        <h3>No Exams Scheduled</h3>
                        <p>Start by setting up your academic year and adding exams, tests, and holidays.</p>
                      </div>
                    );
                  }

                  return (
                    <div className="exams-grid">
                      {exams.map((exam) => (
                    <div key={exam.id} className="exam-event-card">
                      <div className="exam-event-header">
                        <span className="exam-event-type-badge">
                          {getExamTypeLabel(exam.type).toUpperCase()}
                        </span>
                        <div className="exam-event-actions">
                          <button
                            onClick={() => handleEditExam(exam)}
                            className="exam-edit-btn"
                            title="Edit Exam/Event"
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M18.5 2.5C18.8978 2.10218 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10218 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10218 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteExam(exam.id)}
                            className="exam-delete-btn"
                            title="Delete Exam/Event"
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          </div>
                        </div>
                        
                      <h4 className="exam-event-title">{exam.title}</h4>
                      <p className="exam-event-date-range">
                            {exam.start_date === exam.end_date 
                              ? formatDate(exam.start_date)
                              : `${formatDate(exam.start_date)} - ${formatDate(exam.end_date)}`
                            }
                          </p>
                          {(exam.class_id || exam.section_name) && (
                        <p className="exam-event-assigned">
                              <strong>Assigned to:</strong> {
                                exam.class_id && exam.section_name 
                                  ? `${classes.find(c => c.id === exam.class_id)?.class_name || 'Unknown Class'} - Section ${exam.section_name}`
                                  : exam.class_id 
                                    ? `${classes.find(c => c.id === exam.class_id)?.class_name || 'Unknown Class'}`
                                    : `Section ${exam.section_name}`
                              }
                            </p>
                          )}
                          {exam.subjects && exam.subjects.length > 0 && (
                        <div className="exam-event-subjects">
                          {exam.subjects.map((subject, index) => (
                            <div key={index} className="exam-event-subject-row">
                              <span className="exam-event-subject-name">{subject.name}</span>
                              <span className="exam-event-subject-datetime">
                                {formatDate(subject.date)} • {subject.time}
                              </span>
                                  </div>
                                ))}
                        </div>
                                )}
                              </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmPopup && (
        <div className="delete-popup-overlay" onClick={cancelDeleteExam}>
          <div className="delete-popup" onClick={(e) => e.stopPropagation()}>
            <div className="delete-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="#EF4444"/>
              </svg>
            </div>
            <h3 className="delete-popup-title">Delete Exam/Event?</h3>
            <p className="delete-popup-message">
              Are you sure you want to delete this exam/event? This action cannot be undone.
            </p>
            <div className="delete-popup-buttons">
              <button className="delete-popup-cancel-btn" onClick={cancelDeleteExam}>
                Cancel
                          </button>
              <button className="delete-popup-confirm-btn" onClick={confirmDeleteExam}>
                Delete
                          </button>
                        </div>
                      </div>
                  </div>
                )}

      {/* Delete Success Popup */}
      {showDeleteSuccessPopup && (
        <div className="success-popup-overlay">
          <div className="success-popup">
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              </div>
            <h3 className="success-popup-title">Success!</h3>
            <p className="success-popup-message">
              Exam/Event deleted successfully!
            </p>
            </div>
          </div>
      )}

      {/* General Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-overlay">
          <div className="success-popup">
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
        </div>
            <h3 className="success-popup-title">Success!</h3>
            <p className="success-popup-message">
              {successMessage}
            </p>
      </div>
        </div>
      )}
    </div>
  );
}

export default ExamSchedule;
